import { useState } from 'react';

import Alert from 'components/Alert/Alert';
import { Button, Input, InputGroup } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';

import { IntegrationCard } from '../IntegrationCard/IntegrationCard';

const copy = value => navigator.clipboard.writeText(value ?? '');

const resultOf = action => {
  if (action?.error) {
    // createThunk rejects with rejectWithValue, so error.message is the generic 'Rejected'
    // and the server's reason is on payload.
    const reason = action.payload?.message ?? action.payload ?? action.error.message;
    throw new Error(typeof reason === 'string' ? reason : 'Request failed');
  }
  return action?.payload ?? action;
};

const ReadOnlyRow = ({ label, value }) => (
  <InputGroup title={label} className="w-full">
    <div className="d-flex align-items-center">
      <Input className="u-width-100" inputProps={{ value: value ?? '', readOnly: true }} />
      <IconButton
        icon="clipboard"
        className="u-margin-left--small"
        onClick={() => copy(value)}
        tip="Copy"
        placement="bottom"
      />
    </div>
  </InputGroup>
);

export const CornerstoneIntegration = ({
  config,
  integration,
  connectCornerstone = () => Promise.resolve({}),
  disconnectCornerstone = () => Promise.resolve(),
  regenerateCornerstonePassword = () => Promise.resolve({}),
  updatePartnersConfig = () => Promise.resolve(),
  setLoading = () => {},
  popUpMessage = () => {},
}) => {
  const [enabled, setEnabled] = useState(!!integration.enabled);
  const [generated, setGenerated] = useState(null);
  const [edgeApiKey, setEdgeApiKey] = useState(integration.cornerstone_edge_api_key ?? '');
  const [showRegenerate, setShowRegenerate] = useState(false);

  const onToggle = async event => {
    const next = event.target.checked;
    if (next === enabled) return;

    if (next) {
      setLoading(true);
      try {
        const data = resultOf(await connectCornerstone());
        setGenerated(data);
        setEnabled(true);
      } catch (e) {
        popUpMessage('Failed to enable Cornerstone', e?.message ?? 'Try again', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        resultOf(await disconnectCornerstone());
        setEnabled(false);
        setGenerated(null);
        setEdgeApiKey('');
      } catch (e) {
        popUpMessage('Failed to disconnect Cornerstone', e?.message ?? 'Try again', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveEdgeApiKey = async () => {
    setLoading(true);
    try {
      resultOf(await updatePartnersConfig({ cornerstone_edge_api_key: edgeApiKey }));
      popUpMessage('Saved', 'Edge API Key saved', 'success');
    } catch (e) {
      popUpMessage('Failed to save Edge API Key', e?.message ?? 'Try again', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    setShowRegenerate(false);
    setLoading(true);
    try {
      const data = resultOf(await regenerateCornerstonePassword());
      setGenerated({
        ...generated,
        password: data.password,
        username: integration.cornerstone_basic_auth_username,
        base_service_url: integration.base_service_url,
      });
    } catch (e) {
      popUpMessage('Failed to regenerate password', e?.message ?? 'Try again', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IntegrationCard {...config} enabled={enabled} onToggle={onToggle}>
      <Alert
        show={showRegenerate}
        title="Regenerate password?"
        message="A new password will be generated and shown once. The old password stops working immediately, so you'll need to update CSOD admin with the new value."
        ok="Regenerate"
        type="warning"
        onOk={handleConfirmRegenerate}
        cancel="Cancel"
        onCancel={() => setShowRegenerate(false)}
      />

      {generated && (
        <div className="u-margin-bottom">
          <div className="alert alert-warning">
            <strong>Save these credentials now.</strong> The password is shown only once.
          </div>
          <ReadOnlyRow label="Base Service URL" value={generated.base_service_url} />
          <ReadOnlyRow label="Username" value={generated.username} />
          <ReadOnlyRow label="Password" value={generated.password} />
          <Button type="secondary" className="w-full" onClick={() => setGenerated(null)}>
            I&apos;ve saved these
          </Button>
        </div>
      )}

      {enabled && !generated && (
        <div className="w-full">
          <ReadOnlyRow label="Base Service URL" value={integration.base_service_url} />
          <ReadOnlyRow label="Username" value={integration.cornerstone_basic_auth_username} />

          <InputGroup title="Edge API Key" className="w-full">
            <div className="d-flex align-items-center">
              <Input
                className="u-width-100"
                inputProps={{
                  value: edgeApiKey,
                  onChange: e => setEdgeApiKey(e.target.value),
                  placeholder: 'Paste from CSOD admin after saving the connector',
                }}
              />
            </div>
          </InputGroup>
          <Button
            type="secondary"
            className="w-full u-margin-bottom"
            onClick={handleSaveEdgeApiKey}>
            Save Edge API Key
          </Button>

          <div className="d-flex" style={{ gap: '0.5rem' }}>
            <Button type="neutral" className="w-full" onClick={() => setShowRegenerate(true)}>
              Regenerate password
            </Button>
          </div>
        </div>
      )}
    </IntegrationCard>
  );
};
